import { Collapse, Grid, IconButton, makeStyles, Tab, Tabs, Typography } from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import { useEffect, useRef, useState } from 'react';

import {

  auth,
  dateRangeForm,
  formApi,
  formatDate,
  getBillingDft,
  getWorksheet,
  lightGreen,
  MultiSelect,
  RangePicker,
  requestActivity,
  Text,
  utcDateString,
  withTooltip,
  worksheetTypeForm,
} from '../../core';

import CoderProductivity from '../../core/coder-productivity';
import { Visit, Worksheet } from '../../core/domain';
import { OrganizationApi, OrganizationSelection } from '../../core/organization-api';
import { Page } from '../../core/page';
import { PagedTable, TableColumn } from '../../core/paged-table';
import { history } from '../../core/router';
import { setCurrentVisitSession } from '../../core/visit-context';
import { VisitSearchResult, worksheetQueue } from '../../core/visit-queue';
import { currentWorkflowPrompt, isAuditWorkflow } from '../../core/workflow-api';
import { PeriodSearch } from '../common/forms';

import { DateRange, Refresh } from '@material-ui/icons';
import { Navigate } from 'react-router';
import create, { UseStore } from 'zustand';
import { userProfileApi } from '../../api/user-profile-api';
import { AuditedReport, AuditorReport } from '../audit/audit-report';
import { auditWorksheetSearchApi } from '../audit/audit-worhsheet-search-api';
import { customDateRangeApi, CustomDateRangeDialog, RangeItem } from '../../core/data-range-selector';
import { AuditSearch, MyAuditSearch } from '../audit-list/audit-search';
import { SideDrawer } from '../common/drawers';
import { LogoutLink } from '../common/navigation';
import { OrganizationSelect, OrganizationSelectAudit } from '../common/organization-select';
import { SideBar } from '../common/side-bars';
import { auditReviewColumns } from './visit-list-api';
import { VisitListApi, visitListApiActivity } from './visit-list-api-activity';
import { VisitSearch } from './visit-search';
import { ReviewTable } from '../../core/review-table';
import { auditReportCall } from '../audit/audit-report-api';
import { AuditPagedTable } from '../audit/audit-repor-paged-table';
import { openReadonlyVisit } from '../audit/audit-batch';
import { auditBatch } from '../../core/audit-batch-api';

interface VisitPageProps extends VisitListApi {
  filterOpen?: boolean;
  hideFilter?: boolean;
  audit?: boolean;
  workfow?: string;
  showFlags?: boolean;
  onLogout?: () => void;
  onVisitClick?: (a: Visit) => void;
}

type TabDataProps = {
  tab: number;
  sortedArray: boolean;
};

export const tabData = create<TabDataProps>(() => ({
  tab: 0,
  sortedArray: false,
}));

export interface OrganizationSearchHeaderProps {
  title: string | JSX.Element;
  api: UseStore<OrganizationApi>;
  onSearch?: (values: PeriodSearch) => void;
  titleIcon?: any;
  action?: any;
  onLogout?: () => void;
  tab?: number;
  isAudit?: boolean;
}

const tabWidth = 150;
const tabStyles = makeStyles(() => ({
  tabRoot: {
    minWidth: tabWidth,
    width: tabWidth,
  },
}));

function OrganizationSearchHeader(props: OrganizationSearchHeaderProps) {
  const { title, api, onSearch, titleIcon, onLogout, isAudit } = props;
  const query = useRef(
    formApi({
      values: {
        search: '',
      },
      fields: {
        search: {
          id: 'visit-search-input',
          label: '',
          name: 'search',
          placeholder: 'Type enter to start search',
          onEnter: (search) => onSearch({ search }),
          onClear: () => onSearch({ search: null }),
          clearIcon: true,
          startIcon: 'search',
          style: { width: '240px' },
        },
      },
    })
  ).current;
  const { search } = query.controls;
  const period = useRef(
    dateRangeForm({
      label: 'Period',
      onChange: (v) =>
        onSearch({
          search: search.value.getState().value,
          period: v ? v.range : null,
        }),
    })
  ).current;

  const { range } = period.controls;

  const org = api((s) => s.selection);
  const orgName = org?.organization?.name;
  const locationName = org?.location?.name;
  const departmentName = org?.department?.name || org?.department?.code;

  return (
    <Grid container direction="row" wrap="nowrap" spacing={1} alignItems="center" style={{ width: '100%', paddingLeft: '0px', paddingRight: '0px' }}  >
      {titleIcon ? titleIcon : null}
      <Grid item>
        {typeof title === 'string' ? (
          <Typography variant="h6" noWrap>
            {title}
          </Typography>
        ) : (
          title
        )}
      </Grid>
      {orgName && (
        <Grid item>
          <Typography variant="caption" noWrap>
            {orgName}
          </Typography>
        </Grid>
      )}
      {locationName && (
        <Grid item>
          <Typography variant="caption" noWrap>/ {locationName}
          </Typography>
        </Grid>
      )}
      {departmentName && (
        <Grid item>
          <Typography variant="caption" noWrap>/ {departmentName}
          </Typography>
        </Grid>
      )}

      {api && (
        <Grid item style={{ padding: 0, visibility: 'hidden', maxWidth: 300 }}>
          <OrganizationSelect api={api} direction="horizontal" />
        </Grid>
      )}
      <Grid item style={{ flexGrow: 1 }}></Grid>

      {onSearch && (
        <Grid item style={{ padding: 0 }}>
          <Text {...search} />
        </Grid>
      )}

      <Grid item style={{ width: 48, height: 48, backgroundColor: isAudit ? lightGreen : undefined }}>
        <LogoutLink htmlColor="white" onClick={onLogout} />
      </Grid>
    </Grid>
  );
}

export function VisitTable(props: VisitPageProps) {
  const { api, filterOpen, org, columns, pageApi, hideFilter, audit, showFlags } = props;
  const { search, setQuery, updateOrAddSort, updateColumnSort } = api.actions;

  const onRequestPage = ({ pageNumber, pageSize }) => {
    setQuery({ pageNumber, pageSize });
    search();
  };

  const onRequestSort = (sortOrder: any, c: TableColumn) => {
    if (api.data.getState().advancedActive) {
      updateOrAddSort({ ...{ sortOrder }, ...c.data });
    } else {
      updateColumnSort({ ...{ sortOrder }, ...c.data });
    }

    search();
  };

  const onVisitClick = (v: Visit) => {
    if (v) {
      if (props.onVisitClick) {
        if (result?.entries) {
          worksheetQueue.setState({ visit: v });
        }
        props.onVisitClick(v);
      } else {
        setCurrentVisitSession(v);
        history.push('./tool');
      }
    }
  };

  const orgUpdate = (s: OrganizationSelection) => {
    if (!api.data.getState().pendingNavigation) {
      if (s && s.location && s.department) {
        setQuery({
          locationCode: s.location.code,
          departmentCode: s.department && s.department.code,
        });
        search();
      }
    }
  };

  useEffect(() => {
    orgUpdate(org.getState().selection);
    return org.subscribe(
      (s: OrganizationSelection) => {
        orgUpdate(s);
      },
      (s) => s.selection
    );
  }, []);

  const result: VisitSearchResult = worksheetQueue((s) => s.result);
  //TODO: remove test
  // testFlags(result);

  const totalResults = (result && result.totalResults) || 0;
  const selected = worksheetQueue((s) => s.visit);
  const rowIsSelected = (r: Visit) => selected && selected.visitId === r.visitId;

  const codingContainerHeight = filterOpen ? '72vh' : '83vh';

  return (
    <div style={{ height: codingContainerHeight }}>
      {hideFilter ? null : <Collapse in={filterOpen}>{isAuditWorkflow() ? <AuditSearch api={api} /> : <VisitSearch api={api} />}</Collapse>}
    { showFlags  ?
      <ReviewTable
        id="visits"
        containerHeight={codingContainerHeight}
        onRowClick={onVisitClick}
        rowIsSelected={rowIsSelected}
        columns={columns}
        rows={result.entries}
        totalResults={totalResults}
        pageApi={pageApi}
        onRequestPage={onRequestPage}
        onRequestSort={onRequestSort}
        navigationStyle={
          audit
            ? undefined
            : {
              width: '98vw',
              backgroundColor: 'white',
              position: 'absolute',
              bottom: 0,
            }
        }
      />
      :
      <PagedTable
      id="visits"
      containerHeight={codingContainerHeight}
      onRowClick={onVisitClick}
      rowIsSelected={rowIsSelected}
      columns={columns}
      rows={result.entries}
      totalResults={totalResults}
      pageApi={pageApi}
      onRequestPage={onRequestPage}
      onRequestSort={onRequestSort}
      navigationStyle={
        audit
          ? undefined
          : {
            width: '98vw',
            backgroundColor: 'white',
            position: 'absolute',
            bottom: 0,
          }
      }
    />

}
    </div>
  );
}

const toggle = create<any>(() => ({
  open: false,
  bundle: null,
}));
export function AuditWorksheetTable(props: VisitPageProps) {
  const { org, columns, pageApi } = props;

  const tabValue = tabData.getState().tab;

  useEffect(() => {
    if (tabValue === 2 && auditReportCall.data.getState().worksheetReload === true) {
      auditReportCall.data.setState({ worksheetReload: false });
      search();
    }
  }, [tabValue]);

  const api1 = auditWorksheetSearchApi(pageApi, true);
  const { search, setQuery, updateOrAddSort, updateColumnSort } = api1.actions;

  const onRequestPage = ({ pageNumber, pageSize }) => {
    setQuery({ page: pageNumber, pageSize });
    search();
  };

  const onRequestSort = (sortOrder: any, c: TableColumn) => {
    updateOrAddSort({ ...{ sortOrder }, ...c.data });
    search();
  };

  const onWorksheetClick = async (v: Worksheet) => {
    const open = toggle.getState().open;
    const r = await Promise.all([v, getBillingDft(v.worksheetId), getWorksheet({ worksheetId: v.worksheetId, includeTransitionLog: true })]);

    const details = r[0] || [];
    const documents = r[2] || [];
    const dfts = r[1] || [];
    toggle.setState({ open: !open, bundle: { details: details, documents: documents, dfts: dfts } });
  };
  const orgUpdate = (s: OrganizationSelection) => {
    if (!api1.data.getState().pendingNavigation) {
      if (s && s.location && s.department) {
        setQuery({
          locationCode: s.location.code,
          departmentCode: s.department && s.department.code,
        });
        search();
      }
    }
  };

  useEffect(() => {
    orgUpdate(org.getState().selection);
    return org.subscribe(
      (s: OrganizationSelection) => {
        orgUpdate(s);
      },
      (s) => s.selection
    );
  }, []);

  const result = api1.data.getState().result;

  const [tempColumns, setColumnsTemp] = useState(columns);

  useEffect(() => {
    const columnsTable = api1.data.getState().columns;
    columnsTable?.map((item) => {
      columns.splice(-1, 0, {
        label: item.label,
        render: (r) => findPoints(item.label, r.auditList),
        style: { width: 100, padding: 'default', align: 'center' },
      });
    });

    setColumnsTemp(columns);
  }, [api1.data.getState().columns]);

  function findPoints(label: string, input: any): string {
    let totalPoints = 0;
    let totalPossPoints = 0;
    input.map((item) => {
      if (item.aggregationCategory === label) {
        totalPoints += item?.totalPoints;
        totalPossPoints += item?.totalPossiblePoints;
      }
    });
    return totalPoints + ' / ' + totalPossPoints;
  }

  const totalResults = (result && result.totalCount) || 0;
  const selected = worksheetQueue((s) => s.visit);
  const rowIsSelected = (r: Visit) => selected && selected.visitId === r.visitId;

  const codingContainerHeight = '75vh';
  const bundle = toggle((s) => s.bundle);

  return (
    <div style={{ paddingTop: 15 }}>
      <MyAuditSearch api={api1} />
      <SideDrawer toggle={toggle} anchor="right" sideContent={bundle && <SideBar bundle={bundle} />}></SideDrawer>
      <AuditPagedTable
        id="myAuditWorksheets"
        onRowClick={onWorksheetClick}
        containerHeight={codingContainerHeight}
        rowIsSelected={rowIsSelected}
        columns={tempColumns}
        rows={result?.result}
        totalResults={totalResults}
        pageApi={pageApi}
        onRequestPage={onRequestPage}
        onRequestSort={onRequestSort}
        navigationStyle={{
          width: '98vw',
          backgroundColor: 'white',
          position: 'absolute',
          bottom: 0,
        }}
      />
    </div>
  );
}

export function VisitPage(props: VisitPageProps) {
  const { org, api, onLogout, workflow } = props;

  const { search, setServiceDate } = api.actions;

  const onPeriodSearch = async (args: PeriodSearch) => {
    api.data.setState({ period: args });

    setServiceDate(args.period);
    search();
  };



  const [open, setOpen] = useState(false);
  const toggleOpen = () => {
    setOpen(!open);
    api.data.setState({ advancedActive: !open });
  };

  const title = (
    <Typography style={{ paddingRight: 24 }} variant="h6" noWrap>
      {currentWorkflowPrompt('')}
    </Typography>
  );

  const showCodingList = isAuditWorkflow();
  const tableProps = { ...props, ...{ columns: props.getColumns() } };

  isAuditWorkflow() && auditBatch.actions.refreshBatchList(org.getState().selection);

  return Page({
    mainHeader: (
      <OrganizationSearchHeader
        api={org}
        title={title}
        onLogout={onLogout}
        titleIcon={
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            {
              <IconButton id="open-filters" onClick={toggleOpen} edge="start">
                {open ? <MenuIcon htmlColor="white" id="open_menu_icon" /> : <MenuIcon id="open_menu_icon" />}
              </IconButton>
            }
            <CoderProductivity />
          </div>
        }
        onSearch={onPeriodSearch}
      />
    ),
    main: showCodingList ? <VisitTable {...tableProps} showFlags={false} filterOpen={open} workflow={workflow} /> : <VisitTable {...tableProps} showFlags={codingVisitsApiActivity.showFlags}  filterOpen={open} audit={true} workflow={workflow} />,
  });
}

const api = visitListApiActivity();
export const codingVisitsApiActivity = api;

export function VisitRoute({ signOut }) {
  const data = api.org((s) => s.organizations)?.length > 0;
  // TODO: test
  const user = userProfileApi.data.getState().user;
  api.setUser(user);
  return data && <VisitPage {...api} onLogout={signOut} workfow={api.workflow} />;
}

export function VisitPageRouteActivity({ signOut, activity }) {
  const data = api.org((s) => s.pendingOrComplete);

  if (!auth.isAuthenticated() || !data) {
    if (!data) {
      api.org.getState().loadOrganizations(activity);
    }
    return <Navigate replace to="/" />;
  }
  const reset = worksheetQueue.getState().reset;
  // console.log('loading visit page, checking for reset', reset);
  if (reset) {
    api.resetPage();
    worksheetQueue.setState({ reset: false });
  }

  return <VisitRoute signOut={signOut} />;
}

export interface TabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
}

export function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div style={{ overflow: 'hidden' }} hidden={value !== index} {...other}>
      {children as any}
    </div>
  );
}

export function AuditReview(props: VisitPageProps) {
  const { org, onLogout } = props;
  let tab = tabData.getState().tab;
  const [value, setValue] = useState(tabData.getState().tab);
  const selection = org((s) => s.selection);
  const loading = requestActivity((s) => s.active);
  const api = auditWorksheetSearchApi(props.pageApi, true);
  const title = (
    <Typography style={{ paddingRight: 24 }} variant="h6" noWrap>
      {currentWorkflowPrompt()}
    </Typography>
  );
  const tableProps = {
    ...props,
    ...{ columns: auditReviewColumns() as any, onVisitClick: openReadonlyVisit },
  } as VisitListApi;

  const { actions } = api;
  const { search, reset } = actions;

  const onPeriodSearch = async (args: PeriodSearch) => {
    let startDate = utcDateString(args.period.startDate);
    let endDate = typeof args.period.endDate != 'string' ? utcDateString(args.period.endDate) : args.period.endDate;

    if (tabData.getState().tab === 0) {
      auditReportCall.actions.getAuditedResults({
        locationCode: selection.location.code,
        departmentCode: selection.department.code,
        startDate,
        endDate,
        worksheetType: rangeWorksheet.value.getState().value?.map((item) => {
          return item.name;
        }),
      });
    } else if (tabData.getState().tab === 1) {
      auditReportCall.actions.getAuditorResults({
        locationCode: selection.location.code,
        departmentCode: selection.department.code,
        startDate,
        endDate,
        worksheetType: rangeWorksheet.value.getState().value?.map((item) => {
          return item.name;
        }),
      });
    } else {
      auditReportCall.data.setState({ startDate });
      auditReportCall.data.setState({ endDate });
      api.data.setState((s) => {
        s.query.auditId = null;
        s.query.auditedId = null;
        s.query.providerId = null;
      });
      reset();
      search();
    }
  };

  const onTabChange = (_: any, i: number) => {
    tabData.setState({ tab: i });
    setValue(i);
  };

  function makeTabsReloadable(): void {
    if (tabData.getState().tab === 0) {
      let prop = { ...auditReportCall.data.getState().auditor };
      prop.reload = true;
      auditReportCall.data.setState({ auditor: prop });
      auditReportCall.data.setState({ worksheetReload: true });
      let audited = { ...auditReportCall.data.getState().audited };
      audited.clear = true;
      auditReportCall.data.setState({ audited });
    } else if (tabData.getState().tab === 1) {
      let prop = { ...auditReportCall.data.getState().audited };
      prop.reload = true;
      auditReportCall.data.setState({ audited: prop });
      auditReportCall.data.setState({ worksheetReload: true });
      let auditor = { ...auditReportCall.data.getState().auditor };
      auditor.clear = true;
      auditReportCall.data.setState({ auditor });
    } else {
      let audited = { ...auditReportCall.data.getState().audited };
      audited.reload = true;
      auditReportCall.data.setState({ audited });
      let auditor = { ...auditReportCall.data.getState().audited };
      auditor.reload = true;
      auditReportCall.data.setState({ auditor });
    }
  }

  const tabClasses = tabStyles();

  const period = useRef(
    dateRangeForm({
      onChange: (v) => {
        onPeriodSearch({
          period: v ? v.range : null,
        });
        makeTabsReloadable();
      },
    })
  ).current;

  const { range } = period.controls;
  const worksheetDataList = userProfileApi.worksheetTypesByActivity('AUDITREVIEW', selection.organization.name, [selection.location.name]);
  let list = [];
  worksheetDataList.map((item) => {
    list.push({ name: item, label: item });
  });

  const worksheetType = useRef(
    worksheetTypeForm({
      label: 'Worksheet Type',
      defaultValue: list,
      onChange: (v) => {
        if (v != null) {
          if (tabData.getState().tab === 0) {
            auditReportCall.actions.getAuditedResults({
              locationCode: selection.location.code,
              departmentCode: selection.department.code,
              startDate: utcDateString(auditReportCall.data.getState().startDate),
              endDate: utcDateString(auditReportCall.data.getState().endDate),
              worksheetType: v.map((item) => {
                return item.name;
              }),
            });
            makeTabsReloadable();
          } else if (tabData.getState().tab === 1) {
            auditReportCall.actions.getAuditorResults({
              locationCode: selection.location.code,
              departmentCode: selection.department.code,
              startDate: utcDateString(auditReportCall.data.getState().startDate),
              endDate: utcDateString(auditReportCall.data.getState().endDate),
              worksheetType: v.map((item) => {
                return item.name;
              }),
            });
            makeTabsReloadable();
          } else {
            api.data.setState((s) => {
              s.query.auditId = null;
              s.query.auditedId = null;
              s.query.providerId = null;
            });
            reset();
            search();
          }
        }
      },
      items: list,
    })
  ).current;

  let rangeWorksheet = worksheetType.controls.worksheetType;

  const onRefresh = () => {
    const departmentCode = selection.department.code;
    const locationCode = selection.location.code;
    const worksheetType = auditReportCall.data.getState().worksheetTypes;
    const startDate = auditReportCall.data.getState().startDate;
    const endDate = auditReportCall.data.getState().endDate;
    let auditId =
      tabData.getState().tab === 0 ? auditReportCall.data.getState().audited.auditId : tabData.getState().tab === 1 ? auditReportCall.data.getState().auditor.auditId : null;
    let providerId =
      tabData.getState().tab === 0 ? auditReportCall.data.getState().audited.providerId : tabData.getState().tab === 1 ? auditReportCall.data.getState().auditor.providerId : null;
    let auditedId = auditReportCall.data.getState().auditor.auditedId;

    if (tabData.getState().tab === 0) {
      auditReportCall.actions.getAuditedResults({
        departmentCode,
        locationCode,
        worksheetType,
        startDate,
        endDate,
        auditId,
        providerId,
      });
    } else if (tabData.getState().tab === 1) {
      auditReportCall.actions.getAuditorResults({
        departmentCode,
        locationCode,
        worksheetType,
        startDate,
        endDate,
        auditId,
        providerId,
        auditedId,
      });
    }
  };

  useEffect(() => {
    range.variant = 'outlined';
    rangeWorksheet.variant = 'outlined';
    setValue(tabData.getState().tab);
  }, [tab]);

  useEffect(() => {
    if (selection.location && selection.department && customDateRangeRef.data.getState().selected.startDate && customDateRangeRef.data.getState().selected.endDate) {
      if (tabData.getState().tab === 0) {
        auditReportCall.actions.getAuditedResults({
          locationCode: selection.location.code,
          departmentCode: selection.department.code,
          startDate: utcDateString(auditReportCall.data.getState().startDate),
          endDate: utcDateString(auditReportCall.data.getState().endDate),
          worksheetType: rangeWorksheet.value.getState().value?.map((item) => {
            return item.name;
          }),
        });
        makeTabsReloadable();
      } else if (tabData.getState().tab === 1) {
        auditReportCall.actions.getAuditorResults({
          locationCode: selection.location.code,
          departmentCode: selection.department.code,
          startDate: utcDateString(auditReportCall.data.getState().startDate),
          endDate: utcDateString(auditReportCall.data.getState().endDate),
          worksheetType: rangeWorksheet.value.getState().value?.map((item) => {
            return item.name;
          }),
        });
        makeTabsReloadable();
      } else {
        api.data.setState((s) => {
          s.query.auditId = null;
          s.query.auditedId = null;
          s.query.providerId = null;
        });
        reset();
        search();
      }
    }
  }, [selection.department, selection.location]);

  const customDateRangeRef = useRef(
    customDateRangeApi({
      onChange: (a: RangeItem) => {
        if (a.startDate && a.endDate) {
          reset();
          onPeriodSearch({
            period: { startDate: new Date(a.startDate), endDate: new Date(a.endDate) },
          });
          makeTabsReloadable();
          range.value.setState({ value: { range: { startDate: a.startDate, endDate: a.endDate }, label: range.value.getState().value?.label } });
        }
      },
    })
  ).current;

  customDateRangeRef.data.setState({
    selected: {
      label: range.value.getState().value?.label,
      startDate: range.value.getState().value?.range?.startDate as Date,
      endDate: range.value.getState().value?.range?.endDate as Date,
    },
  });

  const dateTimeControl = (
    <>
      <div style={{ width: '70%', minWidth: 1120, flexWrap: 'nowrap', display: 'flex', flexDirection: 'row', alignItems: 'flex-start', paddingBottom: 10 }}>
        <Grid item container direction="row" style={{ flexWrap: 'nowrap', paddingBottom: 6, paddingTop: 6, width: '100%', maxWidth: 350 }} alignItems="flex-start">
          <Grid item>
            <div style={{ color: 'gray', paddingBottom: 5, paddingLeft: 5 }}>Audit Date Range:</div>
            <RangePicker c={range} customDateRange={customDateRangeRef} />
          </Grid>
        </Grid>
        <CustomDateRangeDialog {...customDateRangeRef} />
        <Grid item style={{ paddingLeft: 15 }}>
          <OrganizationSelectAudit api={org as any} direction="horizontal" controlWidth="100%" partial={true} variant="outlined" />
        </Grid>

        <Grid item style={{ paddingLeft: 15, paddingRight: 15, paddingTop: 6 }}>
          <div style={{ color: 'gray', paddingBottom: 8, paddingLeft: 5 }}>Worksheet Type:</div>
          <MultiSelect {...rangeWorksheet} />
        </Grid>
        {value != 2 && (
          <IconButton onClick={onRefresh} style={{ width: '12px', height: '12px', top: 32 }}>
            {withTooltip('Refresh', <Refresh color="primary" fontSize="large" style={{ paddingTop: 4 }} />)}
          </IconButton>
        )}
      </div>
      <Grid item container direction="column" spacing={2} style={{ width: '100%', flexWrap: 'nowrap', overflow: 'hidden' }} alignItems="flex-start" justifyContent="space-between">
        <div style={{ paddingLeft: 10, display: 'flex', flexDirection: 'row' }}>
          <DateRange color="disabled" />
          <div style={{ fontSize: 14, marginLeft: 10 }}>
            {formatDate(range.value.getState().value?.range?.startDate) + ' - ' + formatDate(range.value.getState().value?.range?.endDate)}
          </div>
        </div>
      </Grid>
    </>
  );

  return Page({
    loading: loading,
    mainHeader: (
      <>
        <OrganizationSearchHeader api={org} title={title} onLogout={onLogout} isAudit={true} onSearch={null} />
        <Grid style={{ position: 'absolute', right: 48, top: 0 }}>
          <Tabs value={value} style={{ backgroundColor: lightGreen }} onChange={onTabChange}>
            <Tab
              label="Audited"
              classes={{
                root: tabClasses.tabRoot,
              }}
            />

            <Tab
              label="Auditor"
              classes={{
                root: tabClasses.tabRoot,
              }}
            />

            <Tab
              label="Worksheets"
              classes={{
                root: tabClasses.tabRoot,
              }}
            />
          </Tabs>
        </Grid>
      </>
    ),
    main: (
      <div
        style={{
          width: '100%',
          height: '90vh',
          overflowY: 'auto',
        }}
      >
        {dateTimeControl}
        <TabPanel key={0} value={value} index={0}>
          <AuditedReport org={org} />
        </TabPanel>
        <TabPanel key={1} value={value} index={1}>
          <AuditorReport org={org} />
        </TabPanel>
        <TabPanel key={2} value={value} index={2}>
          <AuditWorksheetTable {...tableProps} />
        </TabPanel>
      </div>
    ),
  });
}

export function AuditReviewPageRoute({ signOut }) {
  if (!auth.isAuthenticated()) {
    return <Navigate replace to="/" />;
  }
  return <AuditReview {...api} onLogout={signOut} />;
}
